import type React from "react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { AlertCircle, Building2, MapPin, Pencil, Wallet } from "lucide-react";
import { partnerDirectorySchema } from "../../schemavalidations/partnerDirectorySchema";
import type {
  PartnerInfoWithClientAndReg,
  Partner,
  PartnerInfoWithClientCode,
} from "../../types";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import { useAppSelector } from "../../hooks/useStore";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import type { PlacesApiStatus } from "../GooglePlaces/GooglePlacesAutoComplete";

interface PartnerFormProps {
  partner?: Partner;
  onSubmit: (
    data:
      | PartnerInfoWithClientAndReg
      | PartnerInfoWithClientCode
      | Partial<Partner>,
  ) => Promise<void>;
  loading: boolean;
  isEditMode?: boolean;
  requireRegistrationCode?: boolean;
  /**
   * Show + validate the billing block (VAT number, payment terms, billing
   * email). Only the Partners page needs it (invoicing). The Addresses page
   * reuses this same form and must NOT show or require billing — so it defaults
   * to false, and those fields stay out of the payload (its API rejects them).
   */
  withBilling?: boolean;
  /**
   * Show the "Mark as forwarder" checkbox and include `isForwarder` in the
   * submitted payload. Only the Partners page needs it. The Addresses page
   * reuses this same form and its API rejects `isForwarder` — so it defaults to
   * false, and the field is stripped from the payload when off.
   */
  withForwarder?: boolean;
  /**
   * Starting values for a *new* record — e.g. a company read off a transport
   * order, so "Add to directory" opens on what the document already says
   * rather than on a blank form. Ignored in edit mode, where `partner` rules.
   * A location among them counts as picked, so it is not asked for again.
   */
  initialValues?: Partial<Partner>;
}

const GOOGLE_PLACES_API_KEY =
  import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

export const PartnerForm: React.FC<PartnerFormProps> = ({
  partner,
  onSubmit,
  loading,
  isEditMode = false,
  requireRegistrationCode,
  withBilling = false,
  withForwarder = false,
  initialValues,
}) => {
  const { t } = useTranslation(["partners", "common"]);
  const [readonly, setReadonly] = useState(isEditMode);
  const { countries } = useAppSelector((state) => state.company);

  /** Address box focus/interaction state, used to time the nudges below. */
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const [addressBlurred, setAddressBlurred] = useState(false);
  /**
   * Whether the address text has been typed into since it was last set from a
   * pick or loaded from the record. Separates "mid-word" — where a warning
   * would be premature — from "this stored address has no location", which is
   * worth saying as soon as the form opens.
   */
  const [addressEdited, setAddressEdited] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [placesStatus, setPlacesStatus] = useState<PlacesApiStatus>("loading");
  /**
   * Places occasionally returns a place with no geometry. The user did pick a
   * suggestion, so "pick from the suggestions" would be a dead end — this
   * swaps the hint for one that asks for a more specific address.
   *
   * It does NOT waive the requirement: saving still needs coordinates (see
   * `enforceAddressPick`), because the API rejects a directory write without
   * them either way.
   */
  const [pickedWithoutGeometry, setPickedWithoutGeometry] = useState(false);
  /**
   * A suggestion was picked but its details lookup failed — same treatment:
   * a different hint, not a different outcome.
   */
  const [placeLookupFailed, setPlaceLookupFailed] = useState(false);
  /**
   * The last suggestion picked, so that retyping/undoing back to that exact
   * text restores its coordinates instead of silently losing them.
   */
  const lastPickRef = useRef<{
    text: string;
    coordinates?: { latitude: number; longitude: number };
  } | null>(null);

  const handleUnlockFields = () => setReadonly(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<PartnerInfoWithClientAndReg | PartnerInfoWithClientCode>({
    resolver: yupResolver(partnerDirectorySchema) as any,
    context: {
      isCreateMode: !isEditMode,
      requireRegistrationCode,
      withBilling,
    },
  });

  const watchAddressStreet = watch("address.street");
  // Present only once a Places suggestion has been picked (and cleared again
  // the moment the address text is edited by hand).
  const coordinates = watch("address.coordinates") as
    { latitude: number; longitude: number } | undefined;
  const hasCoordinates =
    typeof coordinates?.latitude === "number" &&
    typeof coordinates?.longitude === "number";

  const missingCoordinates = !hasCoordinates;

  /**
   * Why there is still no location, so the hint can say something useful
   * instead of repeating "pick from the suggestions" at someone who just did.
   * The requirement itself is never waived here — the API rejects a directory
   * write without coordinates, so promising a save we cannot deliver would only
   * trade an inline hint for a server error.
   */
  const placesUnavailable = !GOOGLE_PLACES_API_KEY || placesStatus === "error";
  const settledWithoutLocation =
    !!watchAddressStreet && (addressBlurred || !addressEdited);
  const locationHintKey = placesUnavailable
    ? "partners:form.locationLookupUnavailable"
    : placeLookupFailed
      ? "partners:form.locationLookupFailed"
      : pickedWithoutGeometry
        ? "partners:form.locationUnavailableForPlace"
        : submitAttempted
          ? "partners:form.locationRequired"
          : settledWithoutLocation
            ? "partners:form.locationNotCaptured"
            : "partners:form.selectFromSuggestions";
  /** Red only once the user has actually been stopped by it. */
  const locationHintTone = submitAttempted
    ? "text-red-600"
    : placesUnavailable ||
        placeLookupFailed ||
        pickedWithoutGeometry ||
        settledWithoutLocation
      ? "text-signal"
      : "text-gray-500";

  const handleAddressSelect = useCallback(
    (addressData: any) => {
      const coordinates =
        addressData.latitude != null && addressData.longitude != null
          ? {
              latitude: addressData.latitude as number,
              longitude: addressData.longitude as number,
            }
          : undefined;

      setValue("address.street", addressData.street, { shouldValidate: true });
      // City / state / postal code / country are prefilled from the pick but
      // stay hand-editable. A field Places had nothing for keeps whatever the
      // user already typed instead of being blanked out.
      if (addressData.city) setValue("address.city", addressData.city);
      if (addressData.state) setValue("address.state", addressData.state);
      if (addressData.postalCode)
        setValue("address.postalCode", addressData.postalCode);
      if (addressData.country) setValue("address.country", addressData.country);

      // The whole point of forcing a Places pick: capture the map location.
      setValue("address.coordinates", coordinates, { shouldValidate: true });

      lastPickRef.current = { text: addressData.street ?? "", coordinates };
      setPickedWithoutGeometry(!coordinates);
      setPlaceLookupFailed(false);
      setAddressBlurred(false);
      setAddressEdited(false);
    },
    [setValue],
  );

  /**
   * Typing into the address box means the text no longer describes the place
   * that was picked, so the coordinates are stale — drop them. Typing the
   * picked address back exactly (or undoing an edit) restores them, so a stray
   * keystroke does not cost the user another round-trip through the dropdown.
   */
  const handleAddressTyped = useCallback(
    (value: string) => {
      setValue("address.street", value, { shouldValidate: submitAttempted });

      const pick = lastPickRef.current;
      const backAtPick = !!pick && pick.text.trim() === value.trim();
      setValue(
        "address.coordinates",
        backAtPick ? pick!.coordinates : undefined,
        {
          shouldValidate: true,
        },
      );
      setPickedWithoutGeometry(backAtPick ? !pick!.coordinates : false);
      setPlaceLookupFailed(false);
      // Hide the "no location yet" nudge while they are mid-edit; it comes
      // back when they leave the field still without a pick.
      setAddressBlurred(false);
      setAddressEdited(true);
    },
    [setValue, submitAttempted],
  );

  const handlePlacesStatusChange = useCallback(
    (status: PlacesApiStatus) => setPlacesStatus(status),
    [],
  );

  const getCountryId = (
    country: string | { _id: string } | null | undefined,
  ): string => {
    if (!country) return "";
    if (typeof country === "object" && "_id" in country) return country._id;
    return country;
  };

  useEffect(() => {
    if (partner && isEditMode) {
      reset({
        companyName: partner.companyName,
        registerationCode: partner.registerationCode || "",
        emailAddress: partner.emailAddress || "",
        phoneNumber: partner.phoneNumber || "",
        clientCode: partner.clientCode || "",
        vatNumber: partner.vatNumber || "",
        paymentTerms: partner.paymentTerms ?? undefined,
        billingEmail: partner.billingEmail || "",
        isForwarder: partner.isForwarder ?? false,
        address: {
          ...partner.address,
          country: getCountryId(partner.address?.country),
          // Legacy records have none; the form then requires a re-pick.
          coordinates: partner.address?.coordinates ?? undefined,
        },
      });
      // Treat the saved address as the last pick, so editing the text and
      // undoing it does not throw away coordinates the record already had.
      lastPickRef.current = partner.address?.coordinates
        ? {
            text: partner.address.street ?? "",
            coordinates: partner.address.coordinates,
          }
        : null;
      setPickedWithoutGeometry(false);
      setPlaceLookupFailed(false);
      setAddressBlurred(false);
      setAddressEdited(false);
      setSubmitAttempted(false);
    }
  }, [partner, isEditMode, reset]);

  // A new record that starts from known values. Applied once, on open: the
  // form owns the values from then on.
  //
  // Only fields this form actually has are seeded. react-hook-form submits
  // every value it holds, rendered or not, so seeding the billing block on an
  // Addresses form would send `billingEmail` / `vatNumber` to an API that
  // refuses them.
  useEffect(() => {
    if (isEditMode || !initialValues) return;
    const address = initialValues.address;
    reset({
      companyName: initialValues.companyName || "",
      registerationCode: initialValues.registerationCode || "",
      emailAddress: initialValues.emailAddress || "",
      phoneNumber: initialValues.phoneNumber || "",
      ...(withBilling ? { vatNumber: initialValues.vatNumber || "" } : {}),
      address: {
        street: address?.street || "",
        city: address?.city || "",
        state: address?.state || "",
        postalCode: address?.postalCode || "",
        country: getCountryId(address?.country),
        coordinates: address?.coordinates ?? undefined,
      },
    } as PartnerInfoWithClientAndReg);
    lastPickRef.current = address?.coordinates
      ? { text: address.street ?? "", coordinates: address.coordinates }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Saving with a hand-typed address that was never picked from the dropdown:
   * stop, point at the address field and focus it, rather than silently storing
   * a partner with no map location.
   */
  const enforceAddressPick = (): boolean => {
    setSubmitAttempted(true);
    if (!missingCoordinates) return true;
    addressInputRef.current?.focus();
    addressInputRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
    return false;
  };

  const onFormSubmit = (
    data: PartnerInfoWithClientAndReg | PartnerInfoWithClientCode,
  ): Promise<void> => {
    if (!enforceAddressPick()) return Promise.resolve();
    // The shared yup schema defaults `isForwarder` to false, but only the
    // Partners page should send it — the Addresses API rejects unknown keys.
    if (!withForwarder) {
      const { isForwarder, ...rest } = data as {
        isForwarder?: boolean;
      } & typeof data;
      return onSubmit(rest);
    }
    return onSubmit(data);
  };

  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  /**
   * Read-only mode is the edit form with everything switched off, so that what
   * a record looks like and what you change are the same screen. What it drops
   * is the instructions — a placeholder reading "Enter phone number" is one, and
   * so are the hints under the address and VAT fields.
   */
  const ph = (text: string) => (readonly ? "" : text);
  /**
   * A select needs its empty option to keep existing, or a record with no value
   * would display the first real option as though it were set.
   */
  const selectPh = (text: string) => (readonly ? "—" : text);

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit, () => {
        // Other fields failed too: still mark the attempt so the address hint
        // switches from a nudge to an error alongside them.
        setSubmitAttempted(true);
      })}
    >
      <div className="p-3 space-y-2">
        <SectionCard
          icon={Building2}
          title={t("partners:form.companyInformation")}
        >
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.companyName")}
              required
              error={errors.companyName?.message}
            >
              <Input
                {...register("companyName")}
                size="sm"
                error={!!errors.companyName}
                placeholder={ph(t("partners:form.enterCompanyName"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.registrationCode")}
              required={requireRegistrationCode}
              error={errors.registerationCode?.message}
            >
              <Input
                {...register("registerationCode")}
                size="sm"
                error={!!errors.registerationCode}
                placeholder={ph(t("partners:form.enterRegistrationCode"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.clientCode")}
              error={errors.clientCode?.message}
            >
              <Input
                {...register("clientCode")}
                size="sm"
                error={!!errors.clientCode}
                placeholder={ph(t("partners:form.enterClientCode"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.emailAddress")}
              error={errors.emailAddress?.message}
            >
              <Input
                {...register("emailAddress")}
                type="email"
                size="sm"
                error={!!errors.emailAddress}
                placeholder={ph(t("partners:form.enterEmail"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.phoneNumber")}
              error={errors.phoneNumber?.message}
            >
              <Input
                {...register("phoneNumber")}
                type="tel"
                size="sm"
                error={!!errors.phoneNumber}
                placeholder={ph(t("partners:form.enterPhone"))}
                readOnly={readonly}
              />
            </FormField>

            {withForwarder && (
              <FormField
                layout="inline"
                size="sm"
                label={t("partners:form.forwarder")}
              >
                <label
                  className={`flex items-center gap-2 pt-1 text-[12px] select-none ${
                    readonly
                      ? "text-[#8A9590] cursor-not-allowed"
                      : "text-gray-600"
                  }`}
                >
                  <input
                    {...register("isForwarder")}
                    type="checkbox"
                    disabled={readonly}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                  {t("partners:form.markAsForwarderHint")}
                </label>
              </FormField>
            )}
          </div>
        </SectionCard>

        <SectionCard
          icon={MapPin}
          title={t("partners:form.addressInformation")}
        >
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.address")}
              required
              error={errors.address?.street?.message}
            >
              <div className="space-y-1">
                <AddressAutocompleteField
                  apiKey={GOOGLE_PLACES_API_KEY}
                  value={watchAddressStreet || ""}
                  onChange={handleAddressTyped}
                  onAddressSelect={handleAddressSelect}
                  onBlur={() => setAddressBlurred(true)}
                  onStatusChange={handlePlacesStatusChange}
                  onPlaceSelectError={() => setPlaceLookupFailed(true)}
                  inputRef={addressInputRef}
                  placeholder={ph(t("partners:form.enterAddress"))}
                  disabled={readonly}
                  error={
                    !!errors.address?.street ||
                    (submitAttempted && missingCoordinates)
                  }
                  countries={countries?.flat() || []}
                  size="sm"
                />

                {/* The address must be chosen from the dropdown so its map
                    location is stored. Say where we stand at every step —
                    while typing, on leaving the field, and on save — rather
                    than only failing at the end. */}
                {hasCoordinates ? (
                  <>
                    <p className="flex items-center gap-1 text-[11px] text-success-600">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {t("partners:form.locationCaptured", {
                        lat: coordinates!.latitude.toFixed(5),
                        lng: coordinates!.longitude.toFixed(5),
                      })}
                    </p>
                    {!readonly && (
                      <p className="text-[11px] text-gray-500">
                        {t("partners:form.derivedFieldsEditable")}
                      </p>
                    )}
                  </>
                ) : (
                  !readonly && (
                    <p
                      className={`flex items-center gap-1 text-[11px] ${locationHintTone}`}
                    >
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      {t(locationHintKey)}
                    </p>
                  )
                )}
              </div>
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.city")}
              required
              error={errors.address?.city?.message}
            >
              <Input
                {...register("address.city")}
                size="sm"
                error={!!errors.address?.city}
                placeholder={ph(t("partners:form.enterCity"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.state")}
              required
              error={errors.address?.state?.message}
            >
              <Input
                {...register("address.state")}
                size="sm"
                error={!!errors.address?.state}
                placeholder={ph(t("partners:form.enterState"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.postalCode")}
              error={errors.address?.postalCode?.message}
            >
              <Input
                {...register("address.postalCode")}
                size="sm"
                error={!!errors.address?.postalCode}
                placeholder={ph(t("partners:form.enterPostalCode"))}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("partners:form.country")}
              required
              error={errors.address?.country?.message}
            >
              <Select
                {...register("address.country")}
                size="sm"
                error={!!errors.address?.country}
                options={countryOptions}
                // Native `disabled` (not register's `disabled` option, which
                // would blank the value) — RHF keeps what setValue stored, so
                // the country still submits.
                disabled={readonly}
                placeholder={selectPh(t("partners:form.selectCountry"))}
              />
            </FormField>
          </div>
        </SectionCard>

        {withBilling && (
          <SectionCard icon={Wallet} title={t("partners:form.billingDetails")}>
            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                label={t("partners:form.vatNumber")}
                error={(errors as any).vatNumber?.message}
              >
                <div className="space-y-1">
                  <Input
                    {...register("vatNumber")}
                    size="sm"
                    error={!!(errors as any).vatNumber}
                    placeholder={ph(t("partners:form.enterVatNumber"))}
                    readOnly={readonly}
                  />
                  {!readonly && (
                    <p className="text-[11px] text-gray-500">
                      {t("partners:form.vatNumberHint")}
                    </p>
                  )}
                </div>
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("partners:form.paymentTerms")}
                error={(errors as any).paymentTerms?.message}
              >
                <Select
                  {...register("paymentTerms")}
                  size="sm"
                  error={!!(errors as any).paymentTerms}
                  disabled={readonly}
                  placeholder={selectPh(t("partners:form.selectPaymentTerms"))}
                  options={[7, 14, 30, 60].map((d) => ({
                    value: String(d),
                    label: t("partners:form.daysCount", {
                      count: d,
                      defaultValue: `${d} days`,
                    }),
                  }))}
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("partners:form.billingEmail")}
                error={(errors as any).billingEmail?.message}
              >
                <Input
                  {...register("billingEmail")}
                  type="email"
                  size="sm"
                  error={!!(errors as any).billingEmail}
                  placeholder={ph(t("partners:form.enterBillingEmail"))}
                  readOnly={readonly}
                />
              </FormField>
            </div>
          </SectionCard>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {isEditMode && readonly && (
          <Button
            type="button"
            onClick={handleUnlockFields}
            // The only live control on a screen of greyed-out fields, so it
            // carries the same weight Update does on the editable one.
            variant="primary"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("common:buttons.edit")}
          </Button>
        )}
        {(!isEditMode || (isEditMode && !readonly)) && (
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            variant="primary"
          >
            {isEditMode
              ? t("common:buttons.update")
              : t("common:buttons.create")}
          </Button>
        )}
      </div>
    </form>
  );
};
